/* eslint-disable */
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack } from "@mui/material";




// Redux Actions & Selectors
import { fetchAttendanceRecords } from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsActions";
import { selectAttendanceRecords, selectAttendanceRecordsLoading } from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsSelectors";

import { fetchEmployees } from "store/features/company/employee/employeeActions";
import { selectEmployees } from "store/features/company/employee/employeeSelectors";

import { fetchTopPriorityTasks } from "store/features/company/projectManagement/task/taskActions";
import { selectTopPriorityTasks } from "store/features/company/projectManagement/task/taskSelectors";

import { fetchProjects } from "store/features/company/projectManagement/project/projectActions";
import { selectProjects } from "store/features/company/projectManagement/project/projectSelectors";

import { fetchMyLeaves as fetchAllLeaves } from "store/features/company/leaveManagement/leaveDetails/leaveDetailsActions";
import { selectLeaves as selectAllLeaves } from "store/features/company/leaveManagement/leaveDetails/leaveDetailsSelectors";
import { fetchMyLeaves as fetchMyOwnLeaves } from "store/features/company/leaveManagement/applyLeave/applyLeaveActions";
import { selectMyLeaves } from "store/features/company/leaveManagement/applyLeave/applyLeaveSelectors";

import { fetchRequisitions } from "store/features/company/requisition/requisitionActions";
import { selectRequisitions } from "store/features/company/requisition/requisitionSelectors";

import { selectCurrentUser } from "store/features/auth/authSelectors";
import usePermissions from "hooks/usePermissions";
import { getSecureLocalItem } from "utils/secureSessionStorage";

// Dashboard Widgets
import HeaderBanner from "../../../components/widget/HeaderBanner";
import AttendanceQuickView from "../../../components/widget/AttendanceQuickView";
import MyAttendanceCard from "../../../components/widget/MyAttendanceCard";
import PendingApprovalsWidget from "../../../components/widget/PendingApprovalsWidget";
import UpcomingHolidaysWidget from "../../../components/widget/UpcomingHolidaysWidget";
import QuickShortcutsBar from "../../../components/widget/QuickShortcutsBar";
import TopPriorityTasks from "../../../components/widget/TopPriorityTasks";

const CompanyDashboard = () => {


  const dispatch = useDispatch();




  // Permissions, Features & Role
  const { hasPermission, isFeatureEnabled } = usePermissions();
  const currentUser = useSelector(selectCurrentUser);
  const userRole = getSecureLocalItem("role") || currentUser?.role || currentUser?.role_name || "Company";
  const isEmployeeRole = userRole?.toLowerCase() === "employee";
  const isCompanyRole = userRole?.toLowerCase() === "company";

  const hasAttendanceFeature = isFeatureEnabled("attendance");
  const hasProjectsFeature = isFeatureEnabled("projects");
  const hasLeaveFeature = isFeatureEnabled("leave");
  const hasEmployeesFeature = isFeatureEnabled("employees");

  const canViewAllAttendance = hasPermission("attendance_records.view") || hasPermission("attendance.view") || !isEmployeeRole;
  const showAttendanceQuickView = hasAttendanceFeature && (canViewAllAttendance || isCompanyRole);
  const showMyAttendanceCard = hasAttendanceFeature && !isCompanyRole;

  // Check permission for approving leaves vs applying leaves
  const canApproveLeave =
    hasLeaveFeature && (
      hasPermission("leave_details.approve") ||
      hasPermission("leave_details.view") ||
      hasPermission("leave_details.edit") ||
      hasPermission("leave_details.action") ||
      isCompanyRole
    );

  const canApplyLeave =
    hasLeaveFeature && (
      hasPermission("apply_leave.view") ||
      hasPermission("apply_leave.create") ||
      isEmployeeRole
    );

  // Selectors
  const attendanceRecords = useSelector(selectAttendanceRecords) || [];
  const attendanceLoading = useSelector(selectAttendanceRecordsLoading);
  const employees = useSelector(selectEmployees) || [];
  const topPriorityTasks = useSelector(selectTopPriorityTasks) || [];
  const projects = useSelector(selectProjects) || [];
  const allLeaves = useSelector(selectAllLeaves) || [];
  const myLeaves = useSelector(selectMyLeaves) || [];

  const leaves = canApproveLeave ? allLeaves : canApplyLeave ? myLeaves : [];
  const requisitions = useSelector(selectRequisitions) || [];

  // Data Loading based on permissions & enabled subscription features
  useEffect(() => {
    const token = getSecureLocalItem("token");
    if (!token) return;

    const today = new Date().toISOString().split("T")[0];
    const tenDaysAgo = new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

    // Attendance
    if (hasAttendanceFeature && (showAttendanceQuickView || showMyAttendanceCard)) {
      dispatch(
        fetchAttendanceRecords({
          page: 1,
          limit: 200,
          from_date: tenDaysAgo,
          to_date: today,
        })
      ).catch(() => { });
    }

    // Employees
    if (hasEmployeesFeature && (hasPermission("employee.view") || !isEmployeeRole)) {
      dispatch(fetchEmployees()).catch(() => { });
    }

    // Tasks & Projects
    if (hasProjectsFeature && (hasPermission("task.view") || hasPermission("projects.view"))) {
      dispatch(fetchTopPriorityTasks()).catch(() => { });
      dispatch(fetchProjects()).catch(() => { });
    }

    // Leaves
    if (hasLeaveFeature) {
      if (canApproveLeave) {
        dispatch(fetchAllLeaves()).catch(() => { });
      } else if (canApplyLeave) {
        dispatch(fetchMyOwnLeaves()).catch(() => { });
      }
    }

    // Requisitions
    if (hasEmployeesFeature) {
      dispatch(fetchRequisitions()).catch(() => { });
    }
  }, [dispatch, isEmployeeRole, showAttendanceQuickView, showMyAttendanceCard, canApproveLeave, canApplyLeave, hasAttendanceFeature, hasProjectsFeature, hasLeaveFeature, hasEmployeesFeature]);

  // Current Employee's Attendance record for today
  const myTodayRecord = useMemo(() => {
    if (!currentUser) return null;
    const userId = currentUser.id || currentUser.user_id || currentUser.employee_id;
    return attendanceRecords.find(
      (r) => r.user_id === userId || r.employee_id === userId || r.email === currentUser.email
    ) || attendanceRecords[0] || null;
  }, [attendanceRecords, currentUser]);

  const handleRefreshAttendance = React.useCallback(() => {
    const today = new Date().toISOString().split("T")[0];
    const tenDaysAgo = new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    dispatch(
      fetchAttendanceRecords({
        page: 1,
        limit: 200,
        from_date: tenDaysAgo,
        to_date: today,
      })
    ).catch(() => { });
  }, [dispatch]);

  const showPendingApprovals = (hasLeaveFeature && leaves.length > 0) || (hasEmployeesFeature && requisitions.length > 0);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        maxWidth: 1400,
        width: "100%",
        mx: "auto",
        p: { xs: 2, sm: 2.5, md: 3 },
        boxSizing: "border-box",
      }}
    >
      {/* 1. Header Banner */}
      <HeaderBanner
        user={currentUser}
        userRole={userRole}
        attendanceCount={attendanceRecords.filter((r) => r.check_in).length}
      />

      {/* 2. Quick Actions & Shortcuts */}
      <QuickShortcutsBar userRole={userRole} hasPermission={hasPermission} isFeatureEnabled={isFeatureEnabled} />

      {/* 3. Main Dashboard 2-Column Responsive Layout (Always 50 50 above 900px, 1 col below 900px) */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 1fr) minmax(0, 1fr)",
          },
          gap: 3,
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          alignItems: "start",
        }}
      >
        {/* LEFT COLUMN: EXACTLY 50% width on md+ (900px+), 100% on <900px */}
        <Box sx={{ width: "100%", minWidth: 0, boxSizing: "border-box" }}>
          <Stack spacing={3}>
            {/* Priority Tasks List */}
            {hasProjectsFeature && (
              <TopPriorityTasks tasks={topPriorityTasks} userRole={userRole} />
            )}

            {/* Pending Approvals Widget */}
            {showPendingApprovals && (
              <PendingApprovalsWidget
                userRole={userRole}
                leaves={hasLeaveFeature ? leaves : []}
                requisitions={hasEmployeesFeature ? requisitions : []}
              />
            )}
          </Stack>
        </Box>

        {/* RIGHT COLUMN: EXACTLY 50% width on md+ (900px+), 100% on <900px */}
        <Box sx={{ width: "100%", minWidth: 0, boxSizing: "border-box" }}>
          <Stack spacing={3}>
            {/* My Attendance Card */}
            {showMyAttendanceCard && (
              <MyAttendanceCard
                myTodayRecord={myTodayRecord}
                user={currentUser}
                onRefresh={handleRefreshAttendance}

              />
            )}

            {/* Attendance Quick View / Occupancy */}
            {showAttendanceQuickView && !showMyAttendanceCard && (
              <AttendanceQuickView
                attendanceRecords={attendanceRecords}
                totalEmployees={employees.length}
                loading={attendanceLoading}
              />
            )}

            {/* Upcoming Holidays Widget */}
            {hasLeaveFeature && <UpcomingHolidaysWidget />}

            {/* Desk Occupancy / Presence overview if user can see both */}
            {showMyAttendanceCard && showAttendanceQuickView && (
              <AttendanceQuickView
                attendanceRecords={attendanceRecords}
                totalEmployees={employees.length}
                loading={attendanceLoading}
              />
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default CompanyDashboard;